'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import {
    type ConsentValue,
    denyAdsConsent,
    grantAdsConsent,
    readConsent,
    rememberClickId,
    saveConsent,
    trackPageView,
} from './googleAds';

type ConsentState = ConsentValue | 'loading' | null;

interface GoogleAdsTrackingProps {
    adsId: string;
}

export default function GoogleAdsTracking({ adsId }: GoogleAdsTrackingProps) {
    const pathname = usePathname();

    const [consent, setConsent] = useState<ConsentState>('loading');
    const [settingsOpen, setSettingsOpen] = useState(false);

    /*
     * Die gclid steht nur in der URL der Landingpage. Spätestens nach der ersten
     * clientseitigen Navigation ist sie weg – also sofort merken, noch bevor
     * über den Consent entschieden ist.
     */
    useEffect(() => {
        rememberClickId();
        setConsent(readConsent());
    }, []);

    useEffect(() => {
        if (consent !== 'granted') return;

        /*
         * Idempotent: lädt gtag.js beim ersten Mal, setzt den Consent und holt
         * Conversions nach, die vor der Zustimmung aufgelaufen sind.
         *
         * Bewusst ohne Cleanup: Ein Widerruf beim Verlassen der Seite würde bei
         * jeder Navigation kurzzeitig `denied` in den dataLayer schreiben – genau
         * dazwischen landet das Conversion-Event der neuen Seite und wird verworfen.
         */
        grantAdsConsent(adsId);

        // Bei Soft-Navigation sendet gtag von sich aus keinen Page-View.
        trackPageView(adsId);
    }, [adsId, consent, pathname]);

    function handleConsent(value: ConsentValue): void {
        saveConsent(value);
        setConsent(value);
        setSettingsOpen(false);

        if (value === 'denied') {
            denyAdsConsent();
        }
    }

    if (consent === 'loading') {
        return null;
    }

    const showBanner = consent === null || settingsOpen;

    return (
        <>
            {showBanner && (
                <section
                    role="region"
                    aria-labelledby="google-ads-consent-title"
                    className="cookie-banner"
                >
                    <div className="cookie-banner__content">
                        <h2 id="google-ads-consent-title">
                            Datenschutz-Einstellungen
                        </h2>

                        <p>
                            Wir verwenden Google Ads, um den
                            Erfolg unserer Werbekampagnen zu
                            messen. Google Ads wird erst nach
                            deiner Zustimmung geladen.
                        </p>

                        <p>
                            Weitere Informationen findest du in
                            unserer{' '}
                            <Link href="/datenschutz">
                                Datenschutzerklärung
                            </Link>.
                        </p>

                        <div className="cookie-banner__actions">
                            <button
                                type="button"
                                className="cookie-banner__button cookie-banner__button--ghost"
                                onClick={() =>
                                    handleConsent('denied')
                                }
                            >
                                Ablehnen
                            </button>

                            <button
                                type="button"
                                className="cookie-banner__button cookie-banner__button--primary"
                                onClick={() =>
                                    handleConsent('granted')
                                }
                            >
                                Marketing akzeptieren
                            </button>
                        </div>
                    </div>
                </section>
            )}

            {!showBanner && (
                <button
                    type="button"
                    className="cookie-settings-button"
                    onClick={() => setSettingsOpen(true)}
                >
                    Datenschutz
                </button>
            )}
        </>
    );
}
